// ...existing code...

// Place these inside the Navbar function, after all state and before return:
//   const verticalScrollRef = useRef<HTMLDivElement>(null);
//   useEffect(() => {
//     if (!isVertical || !verticalScrollRef.current) return;
//     const el = verticalScrollRef.current;
//     const updateOverflow = () => {
//       if (el.scrollHeight <= el.clientHeight) {
//         el.style.overflowY = "hidden";
//       } else {
//         el.style.overflowY = "auto";
//       }
//     };
//     updateOverflow();
//     window.addEventListener("resize", updateOverflow);
//     return () => window.removeEventListener("resize", updateOverflow);
//   }, [isVertical, collapsed]);
import { useEffect, useRef, useState, useCallback } from "react";
import {
  GripVertical,
  Plus,
  Minus,
  FileText,
  Lightbulb,
  Clock,
  Box,
  Globe,
  Rocket,
  Shield,
  TrendingUp,
  User,
  Target,
  LayoutPanelLeft,
  LayoutPanelTop,
  MoveDiagonal,
} from "lucide-react";
import { cn } from "../../../utils/utils";
import * as React from "react";

const MOBILE_BREAKPOINT = 768;

export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
    const onChange = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };
    mql.addEventListener("change", onChange);
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  return !!isMobile;
}


type Orientation = "vertical" | "horizontal";

interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const items: NavItem[] = [
  { id: "vision", label: "Vision", href: "#vision", icon: FileText },
  { id: "problem", label: "Problem", href: "#problem", icon: Lightbulb },
  { id: "solution", label: "Solution", href: "#solution", icon: Box },
  { id: "why-now", label: "Why Now", href: "#why-now", icon: Clock },
   { id: "defensibility", label: "Defensibility", href: "#defensibility", icon: Shield },
    { id: "icp", label: "ICP", href: "#icp", icon: Target },
  { id: "market", label: "Market", href: "#market", icon: Globe },
    { id: "traction", label: "Traction", href: "#traction", icon: TrendingUp },
  { id: "strategy", label: "Strategy", href: "#strategy", icon: Rocket },
  { id: "founder", label: "Founder", href: "#founder", icon: User },
 
];

const TOP_LIMIT = 100;
const MARGIN = 30;
const BOTTOM_MARGIN = 30;
const SIDE_MARGIN = 10;

export function Navbar() {


  const isMobile = useIsMobile();
  const [orientation, setOrientation] = useState<Orientation>("vertical");
  const [pos, setPos] = useState({ x: SIDE_MARGIN, y: 100 });
  // Remove size state, use window size for layout
  const [active, setActive] = useState("vision");
  const [collapsed, setCollapsed] = useState(false);

  // Orientation helper (declare once, after orientation state)
  const isVertical = orientation === "vertical";

  const navRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);

  // Ref for vertical scroll container
  const verticalScrollRef = useRef<HTMLDivElement>(null);

  // Orientation helper (must be above useEffect)





  const clampPos = useCallback((x: number, y: number, w: number, h: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Prevent left and right overflow
    const minX = SIDE_MARGIN;
    const maxX = vw - w - SIDE_MARGIN;
    // Prevent bottom of navbar from going below vh - BOTTOM_MARGIN
    const maxY = Math.max(TOP_LIMIT, vh - h - BOTTOM_MARGIN);
    return {
      x: Math.min(Math.max(minX, x), maxX),
      y: Math.min(Math.max(TOP_LIMIT, y), maxY),
    };
  }, []);



  // Drag
  const onDragPointerDown = (e: React.PointerEvent) => {
    e.preventDefault();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
  };

  const onDragPointerMove = (e: React.PointerEvent) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    let w, h;
    if (orientation === "vertical") {
      w = 160;
      // Use actual rendered height for clamping
      h = navRef.current ? navRef.current.offsetHeight : window.innerHeight * 0.8;
    } else {
      let navWidth = horizontalMinWidth;
      if (navRef.current) {
        navWidth = navRef.current.offsetWidth;
      }
      w = navWidth;
      h = 96;
    }
    const next = clampPos(
      dragState.current.origX + dx,
      dragState.current.origY + dy,
      w,
      h,
    );
    setPos(next);
  };

  const onDragPointerUp = (e: React.PointerEvent) => {
    dragState.current = null;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {}
  };



  // Re-clamp on window resize (only for position)
  useEffect(() => {
    const onWinResize = () => {
      setPos((p) => clampPos(p.x, p.y, window.innerWidth, window.innerHeight));
    };
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [clampPos]);

  const toggleOrientation = () => {
    setOrientation((o) => {
      const next = o === "vertical" ? "horizontal" : "vertical";
      setTimeout(() => {
        let w, h;
        if (next === "vertical") {
          w = 160;
          h = navRef.current ? navRef.current.offsetHeight : window.innerHeight * 0.8;
        } else {
          let navWidth = horizontalMinWidth;
          if (navRef.current) {
            navWidth = navRef.current.offsetWidth;
          }
          w = navWidth;
          h = 96;
        }
        setPos((p) => clampPos(p.x, p.y, w, h));
      }, 0);
      return next;
    });
  };



  // Helper for scroll offset
  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    const offset = isMobile ? 80 : 100;
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    } else {
      window.location.hash = `#${id}`;
    }
  };

  useEffect(() => {
    let rafId = 0;

    const updateActiveFromScroll = () => {
      const offset = isMobile ? 96 : 120;
      const sections = items
        .map((item) => ({ id: item.id, el: document.getElementById(item.id) }))
        .filter((section): section is { id: string; el: HTMLElement } => !!section.el);

      if (sections.length === 0) return;

      let nextActive = sections[0].id;

      for (const section of sections) {
        if (section.el.getBoundingClientRect().top <= offset) {
          nextActive = section.id;
        } else {
          break;
        }
      }

      const nearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2;

      if (nearBottom) {
        nextActive = sections[sections.length - 1].id;
      }

      setActive((prev) => (prev === nextActive ? prev : nextActive));
    };

    const onScrollOrResize = () => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateActiveFromScroll);
    };

    updateActiveFromScroll();
    window.addEventListener("scroll", onScrollOrResize, { passive: true });
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("hashchange", onScrollOrResize);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", onScrollOrResize);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("hashchange", onScrollOrResize);
    };
  }, [isMobile]);

  // Mobile: fixed bottom horizontal bar (no drag/resize/toggles)
  if (isMobile) {
    return (
      <div
        className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card/90 backdrop-blur-xl shadow-2xl"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="overflow-x-auto overflow-y-hidden">
          <div className="flex flex-row items-center gap-1 p-2">
            {items.map((item) => {
              const Icon = item.icon;
              const isActive = active === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActive(item.id);
                    scrollToSection(item.id);
                  }}
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-xs font-medium transition-colors shrink-0 min-w-[64px]",
                    isActive
                      ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background",
                      isActive && "border-primary/40 bg-primary/5",
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="truncate max-w-full">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: draggable floating navbar, fills available height/width, scrolls if needed
  // Calculate horizontal width to fit all icons/buttons
  const horizontalMinWidth = items.length * 90 + 60; // 90px per item + 60px for controls/padding
  // For vertical, make it wider for label visibility
  const verticalWidth = 110;

  return (
    <div
      ref={navRef}
      style={
        isVertical
          ? {
              position: "fixed",
              left: pos.x,
              top: pos.y,
              width: verticalWidth,
              overflow: "hidden",
              maxHeight: `86vh`, // Don't fill full window, allow scroll if too tall
              zIndex: 50,
              height: 'auto',
            }
          : collapsed
          ? {
              position: "fixed",
              left: pos.x,
              top: pos.y,
              minWidth: 'auto',
              width: 'auto',
              maxWidth: `calc(100vw - ${pos.x + SIDE_MARGIN}px)`,
              height: 96,
              zIndex: 50,
              overflowX: "auto",
            }
          : {
              position: "fixed",
              left: pos.x,
              top: pos.y,
              minWidth: horizontalMinWidth,
              maxWidth: `calc(100vw - ${pos.x + SIDE_MARGIN}px)`,
              height: 96,
              zIndex: 50,
              overflowX: "auto",
            }
      }
      className={cn(
        "rounded-2xl border border-border bg-card/80 backdrop-blur-xl shadow-2xl flex",
        !isVertical &&
          "[scrollbar-width:thin] [scrollbar-color:#8ec5ff_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#8ec5ff] hover:[&::-webkit-scrollbar-thumb]:bg-[#5aaeff]",
      )}
    >
      <div className={cn("flex w-full h-full", isVertical ? "flex-col" : "flex-row")}> 
        {/* Header / controls */}
        <div
          className={cn(
            "flex items-center justify-center gap-1 shrink-0 select-none border-border",
            isVertical ? "flex-col py-2 w-full border-b" : "flex-row px-2 h-full border-r",
          )}
          {...(isVertical
            ? {
                onPointerDown: onDragPointerDown,
                onPointerMove: onDragPointerMove,
                onPointerUp: onDragPointerUp,
                onPointerCancel: onDragPointerUp,
                style: { cursor: "grab" },
              }
            : {})}
        >
          <button
            onPointerDown={onDragPointerDown}
            onPointerMove={onDragPointerMove}
            onPointerUp={onDragPointerUp}
            onPointerCancel={onDragPointerUp}
            className="p-1.5 rounded-md hover:bg-accent cursor-grab active:cursor-grabbing touch-none"
            aria-label="Drag navbar"
            title="Drag"
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
          <button
            onClick={toggleOrientation}
            className="p-1.5 rounded-md hover:bg-accent"
            aria-label="Toggle orientation"
            title={isVertical ? "Switch to horizontal" : "Switch to vertical"}
          >
            {isVertical ? (
              <LayoutPanelTop className="h-4 w-4 text-muted-foreground" />
            ) : (
              <LayoutPanelLeft className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              if (collapsed) {
                setTimeout(() => {
                  if (isVertical) {
                    // After expanding, ensure enough space for scroll area
                    const navHeight = navRef.current ? navRef.current.scrollHeight : 400;
                    const maxY = window.innerHeight - navHeight - BOTTOM_MARGIN;
                    setPos((p) => {
                      let newY = p.y;
                      if (newY > maxY) newY = Math.max(TOP_LIMIT, maxY);
                      if (newY < TOP_LIMIT) newY = TOP_LIMIT;
                      return { ...p, y: newY };
                    });
                  } else {
                    // Horizontal mode: ensure enough space on the right
                    const navWidth = navRef.current ? navRef.current.offsetWidth : horizontalMinWidth;
                    const maxX = window.innerWidth - navWidth - SIDE_MARGIN;
                    setPos((p) => {
                      let newX = p.x;
                      if (newX > maxX) newX = Math.max(SIDE_MARGIN, maxX);
                      if (newX < SIDE_MARGIN) newX = SIDE_MARGIN;
                      return { ...p, x: newX };
                    });
                  }
                }, 0);
              }
              setCollapsed((c) => !c);
            }}
            className="p-1.5 rounded-md hover:bg-accent"
            aria-label={collapsed ? "Expand" : "Collapse"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            {collapsed ? (
              <Plus className="h-4 w-4 text-muted-foreground" />
            ) : (
              <Minus className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
        </div>

        {/* Items */}
        {!collapsed && (
          <div
            ref={isVertical ? verticalScrollRef : undefined}
            className={cn(
              isVertical
                ? "flex-1 min-h-0 min-w-0 [scrollbar-width:thin] [scrollbar-color:#8ec5ff_transparent] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#8ec5ff] hover:[&::-webkit-scrollbar-thumb]:bg-[#5aaeff]"
                : "flex-1 min-h-0 min-w-0 overflow-x-auto overflow-y-hidden [scrollbar-width:thin] [scrollbar-color:#8ec5ff_transparent] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#8ec5ff] hover:[&::-webkit-scrollbar-thumb]:bg-[#5aaeff]",
            )}
            style={
              isVertical
                ? {
                    maxHeight: "calc(80vh - 50px)",
                    overflowY: "auto",
                    overflowX: "hidden",
                    scrollbarGutter: "stable"
                  }
                : { maxWidth: "100%", minHeight: 0, width: horizontalMinWidth, overflowX: 'auto' }
            }
          >
            <div
              className={cn(
                "flex gap-0.5 p-2 w-full box-border",
                isVertical ? "flex-col items-stretch" : "flex-row items-center justify-start",
              )}
              style={
                isVertical
                  ? undefined
                  : { width: horizontalMinWidth }
              }
            >
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = active === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActive(item.id);
                      scrollToSection(item.id);
                    }}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 rounded-xl px-2 py-2 text-xs font-medium transition-colors shrink-0 min-w-0",
                      isVertical ? "w-full" : "min-w-[90px]",
                      isActive
                        ? "bg-primary/10 text-primary ring-1 ring-primary/30"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-background",
                        isActive && "border-primary/40 bg-primary/5",
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className={cn(
                      "truncate text-center",
                      isVertical ? "max-w-full w-full min-w-0" : "max-w-[80px] min-w-0"
                    )}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
